import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../../../../../service/Api";

//types
export interface Service {
  id: number;
  name: string;
  description: string;
  price: number;
  service_category: string;
  service_category_display: string;
}

export interface ProposalService {
  service: Service;
  quantity: number;
  customizations?: string;
}

interface PaginatedServiceResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Service[];
}

interface UseServicesReturn {
  services: Service[];
  loading: boolean;
  error: string | null;
  nextPageUrl: string | null;
  prevPageUrl: string | null;
  fetchServices: (url?: string) => void;
}


//hook
export const useServices = (
  selectedCategory: string = "all"
): UseServicesReturn => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [prevPageUrl, setPrevPageUrl] = useState<string | null>(null);

  const fetchServices = useCallback(
    async (url?: string) => {
      setLoading(true);
      try {
        let endpoint = url || "/api/packages/package-service/";

        if (!url && selectedCategory !== "all") {
          endpoint += `?service_category=${encodeURIComponent(selectedCategory)}`;
        }

        const response =
          await apiService.get<PaginatedServiceResponse>(endpoint);

        const mapped: Service[] = response.data.results.map((svc) => ({
          ...svc,
          price: Number(svc.price),
        }));

        setServices(mapped);
        setNextPageUrl(response.data.next);
        setPrevPageUrl(response.data.previous);
        setError(null);
      } catch (err: unknown) {
        const errorResponse = (
          err as { response?: { data?: { detail?: string } } }
        )?.response;
        setError(errorResponse?.data?.detail || "Failed to load services.");
      } finally {
        setLoading(false);
      }
    },
    [selectedCategory]
  );

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  return { services, loading, error, nextPageUrl, prevPageUrl, fetchServices };
};